/**
 * 鉴权服务（免登实现参照 banjiang-shop-app 的 loginSystem）
 *
 * 1. 获取钉钉免登 authCode：优先 Dingdocs.base.host.getAuthCode（多维表插件宿主 API），
 *    兜底 dd JSAPI requestAuthCode（与 banjiang-shop-app 的 dd.getAuthCode 同源机制）
 * 2. POST /dingding/login/callback { code, appType: 3 } 换取 JWT token（appType=3 为多维表插件应用）
 * 3. 之后所有后端请求带 Authorization: Bearer <token>，后端据此识别调用人
 * 4. 后端可能在响应头 Authorization 中返回刷新后的 token，自动更新本地缓存
 *
 * 登录失败时页面必须阻断操作（不允许匿名调用业务接口）。
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
// hr-api 网关前缀：插件与后端同域（shop.banjiang.cc），直连不依赖 nginx 的 /api 转发；
// 开发环境由 webpack devServer 代理 /hr-api 到线上
export const HR_API_PREFIX = "/hr-api";
export const SHOP_API_PREFIX = "/shop-api";
const TOKEN_STORAGE_KEY = "banjiang_plugin_token";
// 多维表插件应用的登录类型（后端 DingdingService 按 appType 选择应用凭证换免登 code）
const PLUGIN_APP_TYPE = 3;

let cachedToken: string | null = null;
// 参照 shop-app：登录单飞，避免并发重复免登
let loginPromise: Promise<string> | null = null;

/** 将任意异常规范化为可读文案 */
export function normalizeError(error: unknown, fallback = "未知错误"): string {
  if (error instanceof Error) return error.message || fallback;
  if (typeof error === "string" && error.trim()) return error;
  if (error && typeof error === "object") {
    const anyErr = error as any;
    return anyErr.message || anyErr.errorMessage || JSON.stringify(error);
  }
  return fallback;
}

export function getToken(): string | null {
  if (cachedToken) return cachedToken;
  try {
    cachedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    cachedToken = null;
  }
  return cachedToken;
}

export function setToken(token: string | null) {
  cachedToken = token;
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // localStorage 不可用时仅使用内存缓存
  }
}

/**
 * 从插件 URL 中取 corpId（宿主会把 ?corpId=$CORPID$ 替换为当前组织，参照 banjiang-hr-meeting-plugin）
 */
export function resolveCorpIdFromUrl(): string {
  try {
    const search =
      window.location.search || window.location.hash.split("?")[1] || "";
    return new URLSearchParams(search).get("corpId") || "";
  } catch {
    return "";
  }
}

/**
 * 获取钉钉免登 authCode（浏览器网页版和钉钉客户端均支持，参照 banjiang-hr-meeting-plugin）
 */
async function getDingTalkAuthCode(corpId: string): Promise<string> {
  if (!corpId) {
    throw new Error(
      "未取到 corpId：插件 URL 缺少 ?corpId=$CORPID$，请在插件「用户界面地址」末尾加上该参数"
    );
  }

  // 1) 多维表插件宿主 API（网页版 / 客户端均可用）
  const Dingdocs = (window as any).Dingdocs;
  if (Dingdocs?.base?.host?.getAuthCode) {
    let result: any;
    try {
      result = await Dingdocs.base.host.getAuthCode(corpId);
    } catch (error) {
      throw new Error(
        `免登失败：getAuthCode(corpId=${corpId}) 报错 ${normalizeError(error)}`
      );
    }
    const code = typeof result === "string" ? result : result?.code;
    if (code) return code;
    // 2) 钉钉客户端 JSAPI 兜底（与 banjiang-shop-app 的 dd.getAuthCode 同一免登机制）
    const ddCode = await requestAuthCodeByJsapi(corpId);
    if (ddCode) return ddCode;
    throw new Error(
      `免登失败：getAuthCode 返回空（corpId=${corpId}）。请确认插件域名已加入应用「端内免登地址」白名单，且用正式 https 域名（非 localhost）。`
    );
  }

  const ddCode = await requestAuthCodeByJsapi(corpId);
  if (ddCode) return ddCode;
  throw new Error("免登失败：当前环境不支持获取免登授权码（Dingdocs SDK 未就绪）");
}

/** 钉钉 JSAPI 兜底取免登码，失败返回空字符串 */
async function requestAuthCodeByJsapi(corpId: string): Promise<string> {
  const dd = (window as any).dd;
  if (!dd?.runtime?.permission?.requestAuthCode) return "";
  try {
    const result: any = await new Promise((resolve, reject) => {
      dd.runtime.permission.requestAuthCode({
        corpId,
        onSuccess: resolve,
        onFail: reject,
      });
    });
    return result?.code || "";
  } catch (error) {
    console.warn("dd.requestAuthCode 失败:", error);
    return "";
  }
}

/**
 * 钉钉免登：获取 authCode 并换取后端 JWT token（单飞）
 * @param corpId 企业 corpId（来自 /api/configPermission 返回）
 */
export function loginWithAuthCode(corpId: string): Promise<string> {
  if (loginPromise) return loginPromise;

  loginPromise = (async () => {
    try {
      const code = await getDingTalkAuthCode(corpId);

      const response = await fetch(`${HR_API_PREFIX}/dingding/login/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, appType: PLUGIN_APP_TYPE }),
      });
      if (!response.ok) {
        throw new Error(`登录请求失败: HTTP ${response.status}`);
      }
      const result = await response.json();
      // 参照 shop-app：拿不到 token 视为登录失败
      if (result.code !== 1 || !result.data?.token) {
        throw new Error(result.message || "登录失败：请联系信息技术组");
      }
      setToken(result.data.token);
      return result.data.token as string;
    } catch (error) {
      setToken(null);
      throw error;
    } finally {
      loginPromise = null;
    }
  })();

  return loginPromise;
}

/**
 * 带鉴权的 fetch：有 token 则附带 Authorization 头，并自动更新后端刷新的 token
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers || {});
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const response = await fetch(input, { ...init, headers });

  // 后端可能在响应头中返回刷新后的 token
  const refreshed = response.headers.get("Authorization");
  if (refreshed && refreshed.startsWith("Bearer ")) {
    setToken(refreshed.slice("Bearer ".length));
  }
  return response;
}

export interface CurrentUser {
  employeeId: number;
  name: string;
}

/**
 * 获取当前登录人信息（用于界面展示操作人）
 */
export async function fetchCurrentUser(): Promise<CurrentUser> {
  const response = await authFetch(`${HR_API_PREFIX}/employee/info`);
  if (!response.ok) {
    throw new Error(`获取用户信息失败: HTTP ${response.status}`);
  }
  const result = await response.json();
  if (result.code !== 1 || !result.data) {
    throw new Error(result.message || "获取用户信息失败");
  }
  return {
    employeeId: Number(result.data.employeeId || 0),
    name: String(result.data.name || ""),
  };
}
